import { message, Modal } from 'antd';

import { useRemoveRelatedTermsMutation } from '../../../../graphql/glossaryTerm.generated';
import { useRemoveTypeOfRelationsMutation } from '../../../../graphql/mutations.generated';
import { TermRelationshipType } from '../../../../types.generated';

import { RelatedTermTypes } from '../../shared/components/styled/search/types';
import { useEntityData, useRefetch } from '../../shared/EntityContext';
import { useTranslation } from '../../../../i18n/useTranslation';
import { isInverseRelationship, getRelationshipType } from '../utils';

function useRemoveRelatedEntity(
    termUrn: string,
    relationshipType: TermRelationshipType | RelatedTermTypes | undefined,
    displayName: string,
) {
    const { urn } = useEntityData();
    const { t } = useTranslation();

    const refetch = useRefetch();

    const [removeTypeOfRelations] = useRemoveTypeOfRelationsMutation();
    const [removeRelatedTerms] = useRemoveRelatedTermsMutation();

    function handleRemoveRelatedEntity() {
        const isTypeOf = [RelatedTermTypes.typeOfRelations, RelatedTermTypes.isTypeOf].includes(
            relationshipType as RelatedTermTypes,
        );
        const removeRelation: any = isTypeOf ? removeTypeOfRelations : removeRelatedTerms;

        // This checks if the relationship is an inverse relationship e.g. contains by is inverse of contains
        // So that we can flip the urns and remove the relationship correctly
        const isInverse = isInverseRelationship(relationshipType as RelatedTermTypes);

        // This gets the relationship type to use for the mutation
        const relationshipTypeToUse = getRelationshipType(relationshipType as RelatedTermTypes);

        // This builds the variables for the mutation
        const variables = isTypeOf
            ? {
                  input: isInverse ? { urn: termUrn, nodeUrns: [urn] } : { urn, nodeUrns: [termUrn] },
              }
            : {
                  input: isInverse
                      ? { urn: termUrn, termUrns: [urn], relationshipType: relationshipTypeToUse }
                      : { urn, termUrns: [termUrn], relationshipType: relationshipTypeToUse },
              };

        removeRelation({ variables })
            .catch((e) => {
                message.destroy();
                message.error({
                    content: t('Failed to remove: \n {{message}}', { message: e.message || '' }),
                    duration: 3,
                });
            })
            .finally(() => {
                message.loading({
                    content: t('Removing...'),
                    duration: 2,
                });
                setTimeout(() => {
                    refetch();
                    message.success({
                        content: t('Removed Successfully!'),
                        duration: 2,
                    });
                }, 2000);
            });
    }

    function onRemove() {
        Modal.confirm({
            title: t('Remove {{displayName}}', { displayName }),
            content: t('Are you sure you want to remove this?'),
            onOk() {
                handleRemoveRelatedEntity();
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            maskClosable: true,
            centered: true,
            closable: true,
        });
    }

    return { onRemove };
}

export default useRemoveRelatedEntity;
